import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiDollarSign, FiUsers, FiRotateCcw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function Tuition() {
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const [tuitions, setTuitions] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pointBalance, setPointBalance] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [editingTuition, setEditingTuition] = useState(null);
  const [selectedTuition, setSelectedTuition] = useState(null);
  const [refundData, setRefundData] = useState({ amount: '', reason: '' });
  const [bulkData, setBulkData] = useState({
    classGroupId: '',
    billingMonth: format(new Date(), 'yyyy-MM'),
    dueDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 10), 'yyyy-MM-dd'),
    tuitionAmount: ''
  });
  const [bulkResult, setBulkResult] = useState(null);
  const [filter, setFilter] = useState({ status: '', month: format(new Date(), 'yyyy-MM') });
  const [formData, setFormData] = useState({
    studentId: '',
    billingMonth: format(new Date(), 'yyyy-MM'),
    items: [],
    discount: 0,
    discountReason: '',
    dueDate: '',
    note: ''
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paidAt: format(new Date(), 'yyyy-MM-dd'),
    method: 'transfer',
    note: ''
  });

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.month) params.billingMonth = filter.month;

      const [tuitionsRes, studentsRes, classesRes, pointsRes] = await Promise.all([
        api.get('/api/tuition', { params }),
        api.get('/api/users', { params: { role: 'student' } }),
        api.get('/api/class-groups', { params: { active: true } }),
        api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }))
      ]);
      setTuitions(tuitionsRes.data.tuitions || []);
      setStudents(studentsRes.data.users || []);
      setClasses(classesRes.data.classGroups || []);
      setPointBalance(pointsRes.data.balance || 0);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }, [filter.status, filter.month]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openModal = (tuition = null) => {
    if (tuition) {
      setEditingTuition(tuition);
      setFormData({
        studentId: tuition.studentId?._id || tuition.studentId,
        billingMonth: tuition.billingMonth,
        items: tuition.items || [],
        discount: tuition.discount || 0,
        discountReason: tuition.discountReason || '',
        dueDate: tuition.dueDate ? format(new Date(tuition.dueDate), 'yyyy-MM-dd') : '',
        note: tuition.note || ''
      });
    } else {
      setEditingTuition(null);
      const currentMonth = new Date();
      setFormData({
        studentId: '',
        billingMonth: format(currentMonth, 'yyyy-MM'),
        items: [],
        discount: 0,
        discountReason: '',
        dueDate: format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 10), 'yyyy-MM-dd'),
        note: ''
      });
    }
    setShowModal(true);
  };

  // 학생 선택 시 소속 반의 수강료 자동 추가
  const handleStudentChange = (studentId) => {
    setFormData(prev => ({ ...prev, studentId, items: [] }));

    if (studentId) {
      // 학생이 소속된 반 찾기
      const studentClasses = classes.filter(cls =>
        cls.students?.some(s => (s._id || s) === studentId)
      );

      // 수강료가 설정된 반만 자동 추가
      const classItems = studentClasses
        .filter(cls => cls.tuitionFee > 0)
        .map(cls => ({
          name: cls.name,
          amount: cls.tuitionFee,
          classGroupId: cls._id
        }));

      if (classItems.length > 0) {
        setFormData(prev => ({ ...prev, items: classItems }));
      }
    }
  };

  // 항목 추가
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { name: '', amount: 0, classGroupId: '' }]
    }));
  };

  // 항목 수정
  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: field === 'amount' ? Number(value) : value } : item
      )
    }));
  };

  // 반 선택 시 금액 자동 입력
  const handleClassSelect = (index, classGroupId) => {
    const selectedClass = classes.find(c => c._id === classGroupId);
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? {
          ...item,
          classGroupId,
          name: selectedClass?.name || item.name,
          amount: selectedClass?.tuitionFee || item.amount
        } : item
      )
    }));
  };

  // 항목 삭제
  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // 총액 계산
  const getTotalAmount = () => {
    return formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  // 포인트 체크 함수 (수납 알림 발송 시)
  const checkPointsForSend = (notificationType) => {
    const paymentNotif = tenant?.paymentNotification;
    if (!paymentNotif) return true; // 설정이 없으면 발송 안 함

    // 알림 타입에 따라 해당 설정 확인
    let shouldSendPush = false;
    let shouldSendAlimtalk = false;

    if (notificationType === 'request') {
      shouldSendPush = paymentNotif?.push?.enabled && paymentNotif?.push?.request;
      shouldSendAlimtalk = paymentNotif?.alimtalk?.enabled && paymentNotif?.alimtalk?.request;
    } else if (notificationType === 'complete') {
      shouldSendPush = paymentNotif?.push?.enabled && paymentNotif?.push?.complete;
      shouldSendAlimtalk = paymentNotif?.alimtalk?.enabled && paymentNotif?.alimtalk?.complete;
    } else if (notificationType === 'refund') {
      shouldSendPush = paymentNotif?.push?.enabled && paymentNotif?.push?.refund;
      shouldSendAlimtalk = paymentNotif?.alimtalk?.enabled && paymentNotif?.alimtalk?.refund;
    }

    // 발송 설정이 모두 꺼져있으면 포인트 체크 불필요
    if (!shouldSendPush && !shouldSendAlimtalk) {
      return true;
    }

    // 필요 포인트 계산 (알림톡 30P, 푸시만 10P)
    const requiredPoints = shouldSendAlimtalk ? 30 : 10;

    if (pointBalance < requiredPoints) {
      const notificationTypeText =
        notificationType === 'request' ? '수납 요청' :
        notificationType === 'complete' ? '수납 완료' : '환불';

      alert(
        `포인트가 부족합니다.\n\n` +
        `현재 포인트: ${pointBalance.toLocaleString()}P\n` +
        `필요 포인트: ${requiredPoints}P (${notificationTypeText} 알림)\n\n` +
        `※ 알림톡: 30P, 푸시: 10P\n\n` +
        `[확인] → 포인트 충전 페이지로 이동\n` +
        `[취소] → 작업 취소`
      );

      if (window.confirm('포인트 충전 페이지로 이동하시겠습니까?')) {
        navigate('/points');
      }
      return false;
    }

    return true;
  };

  const openPaymentModal = (tuition) => {
    setSelectedTuition(tuition);
    setPaymentData({
      amount: (tuition.finalAmount || tuition.totalAmount) - (tuition.paidAmount || 0),
      paidAt: format(new Date(), 'yyyy-MM-dd'),
      method: 'transfer',
      note: ''
    });
    setShowPaymentModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 항목 검증
    if (formData.items.length === 0) {
      alert('최소 1개 이상의 항목을 추가해주세요');
      return;
    }

    const hasEmptyItem = formData.items.some(item => !item.name || item.amount <= 0);
    if (hasEmptyItem) {
      alert('모든 항목의 이름과 금액을 입력해주세요');
      return;
    }

    // 신규 청구 생성 시 포인트 체크 (수납 요청 알림)
    if (!editingTuition && formData.dueDate) {
      if (!checkPointsForSend('request')) {
        return;
      }
    }

    try {
      if (editingTuition) {
        await api.put(`/api/tuition/${editingTuition._id}`, formData);
      } else {
        await api.post('/api/tuition', formData);
        // 포인트 잔액 새로고침
        const pointsRes = await api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }));
        setPointBalance(pointsRes.data.balance || 0);
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '저장에 실패했습니다');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    // 포인트 체크 (수납 완료 알림)
    if (!checkPointsForSend('complete')) {
      return;
    }

    try {
      await api.post(`/api/tuition/${selectedTuition._id}/payment`, {
        amount: Number(paymentData.amount),
        method: paymentData.method,
        note: paymentData.note
      });

      // 포인트 잔액 새로고침
      const pointsRes = await api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }));
      setPointBalance(pointsRes.data.balance || 0);

      setShowPaymentModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '수납 처리에 실패했습니다');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/api/tuition/${id}`);
      loadData();
    } catch (error) {
      alert('삭제에 실패했습니다');
    }
  };

  // 환불 모달 열기
  const openRefundModal = (tuition) => {
    setSelectedTuition(tuition);
    setRefundData({
      amount: tuition.paidAmount || 0,
      reason: ''
    });
    setShowRefundModal(true);
  };

  // 환불 처리
  const handleRefund = async (e) => {
    e.preventDefault();

    // 포인트 체크 (환불 알림)
    if (!checkPointsForSend('refund')) {
      return;
    }

    if (!window.confirm('정말 환불 처리하시겠습니까?')) return;

    try {
      await api.post(`/api/tuition/${selectedTuition._id}/refund`, {
        amount: Number(refundData.amount),
        reason: refundData.reason
      });

      // 포인트 잔액 새로고침
      const pointsRes = await api.get('/api/points/balance').catch(() => ({ data: { balance: 0 } }));
      setPointBalance(pointsRes.data.balance || 0);

      alert('환불 처리되었습니다');
      setShowRefundModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '환불 처리에 실패했습니다');
    }
  };

  // 학생이 수강중인 반만 필터링
  const getStudentClasses = () => {
    if (!formData.studentId) return classes;
    return classes.filter(cls =>
      cls.students?.some(s => (s._id || s) === formData.studentId)
    );
  };

  // 반별 일괄 청구 모달 열기
  const openBulkModal = () => {
    const currentMonth = new Date();
    setBulkData({
      classGroupId: '',
      billingMonth: format(currentMonth, 'yyyy-MM'),
      dueDate: format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 10), 'yyyy-MM-dd'),
      tuitionAmount: ''
    });
    setBulkResult(null);
    setShowBulkModal(true);
  };

  // 반 선택시 수강료 기본값 설정
  const handleBulkClassChange = (classGroupId) => {
    const selectedClass = classes.find(c => c._id === classGroupId);
    setBulkData(prev => ({
      ...prev,
      classGroupId,
      tuitionAmount: selectedClass?.tuitionFee || ''
    }));
  };

  // 선택된 반의 학생 목록 가져오기 (빈 학생 필터링)
  const getClassStudents = () => {
    if (!bulkData.classGroupId) return [];
    const selectedClass = classes.find(c => c._id === bulkData.classGroupId);
    // 삭제된 학생이나 빈 객체 필터링
    return (selectedClass?.students || []).filter(s => s && (s._id || typeof s === 'string'));
  };

  // 반별 일괄 청구 실행
  const handleBulkSubmit = async () => {
    if (!bulkData.classGroupId) {
      alert('반을 선택해주세요');
      return;
    }

    const selectedClass = classes.find(c => c._id === bulkData.classGroupId);
    if (!selectedClass) {
      alert('반을 찾을 수 없습니다');
      return;
    }

    // 삭제된 학생이나 빈 객체 필터링
    const classStudents = (selectedClass.students || []).filter(s => s && (s._id || typeof s === 'string'));
    if (classStudents.length === 0) {
      alert('해당 반에 학생이 없습니다');
      return;
    }

    const tuitionFee = Number(bulkData.tuitionAmount);
    if (!tuitionFee || tuitionFee <= 0) {
      alert('수강료를 입력해주세요');
      return;
    }

    if (!window.confirm(`${selectedClass.name} 반의 ${classStudents.length}명 학생에게 ${bulkData.billingMonth} 청구서를 생성하시겠습니까?\n수강료: ${formatMoney(tuitionFee)}`)) {
      return;
    }

    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const student of classStudents) {
      try {
        await api.post('/api/tuition', {
          studentId: student._id || student,
          billingMonth: bulkData.billingMonth,
          items: [{
            name: selectedClass.name,
            amount: tuitionFee,
            classGroupId: selectedClass._id
          }],
          dueDate: bulkData.dueDate
        });
        successCount++;
      } catch (error) {
        const studentName = student.name || '알 수 없음';
        const reason = error.response?.data?.error || error.message;
        errors.push(`${studentName}: ${reason}`);
        failCount++;
      }
    }

    setBulkResult({ success: successCount, failed: failCount, errors });

    if (errors.length === 0) {
      alert(`${successCount}건의 청구서가 생성되었습니다`);
      setShowBulkModal(false);
    }

    loadData();
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'paid': return 'active';
      case 'partial': return 'warning';
      case 'overdue': return 'danger';
      case 'refunded': return 'refunded';
      case 'cancelled': return 'inactive';
      default: return 'inactive';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'paid': return '완납';
      case 'partial': return '부분납';
      case 'overdue': return '연체';
      case 'refunded': return '환불';
      case 'cancelled': return '취소';
      default: return '미납';
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  // Calculate summary
  const summary = {
    total: tuitions.length,
    paid: tuitions.filter(t => t.status === 'paid').length,
    unpaid: tuitions.filter(t => ['pending', 'overdue'].includes(t.status)).length,
    totalAmount: tuitions.reduce((sum, t) => sum + (t.finalAmount || t.totalAmount || 0), 0),
    paidAmount: tuitions.reduce((sum, t) => sum + (t.paidAmount || 0), 0)
  };

  return (
    <div className="tuition-page">
      <div className="page-header">
        <h2>수납 관리</h2>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={openBulkModal}>
            <FiUsers /> 반별 일괄 청구
          </button>
          <button className="btn btn-primary" onClick={() => openModal()}>
            <FiPlus /> 청구 등록
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{summary.total}</div>
          <div className="stat-label">전체 청구</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-success">{summary.paid}</div>
          <div className="stat-label">완납</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-danger">{summary.unpaid}</div>
          <div className="stat-label">미납/연체</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatMoney(summary.paidAmount)}</div>
          <div className="stat-label">수납액 / {formatMoney(summary.totalAmount)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input
          type="month"
          value={filter.month}
          onChange={(e) => setFilter({ ...filter, month: e.target.value })}
        />
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">전체 상태</option>
          <option value="pending">미납</option>
          <option value="paid">완납</option>
          <option value="partial">부분납</option>
          <option value="overdue">연체</option>
          <option value="refunded">환불</option>
          <option value="cancelled">취소</option>
        </select>
      </div>

      {/* Tuition List */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>학생</th>
              <th>청구월</th>
              <th>청구액</th>
              <th>납부액</th>
              <th>납부기한</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading">로딩 중...</td></tr>
            ) : tuitions.length === 0 ? (
              <tr><td colSpan="7" className="empty">청구 내역이 없습니다</td></tr>
            ) : (
              tuitions.map((tuition) => (
                <tr key={tuition._id}>
                  <td>{tuition.studentId?.name || '-'}</td>
                  <td>{tuition.billingMonth}</td>
                  <td>{formatMoney(tuition.finalAmount || tuition.totalAmount || 0)}</td>
                  <td>{formatMoney(tuition.paidAmount || 0)}</td>
                  <td>{tuition.dueDate ? format(new Date(tuition.dueDate), 'MM/dd') : '-'}</td>
                  <td>
                    <span className={`status ${getStatusClass(tuition.status)}`}>
                      {getStatusText(tuition.status)}
                    </span>
                  </td>
                  <td>
                    {tuition.status !== 'paid' && (
                      <button className="icon-btn" onClick={() => openPaymentModal(tuition)} title="수납 처리">
                        <FiDollarSign />
                      </button>
                    )}
                    {tuition.paidAmount > 0 && (
                      <button className="icon-btn warning" onClick={() => openRefundModal(tuition)} title="환불">
                        <FiRotateCcw />
                      </button>
                    )}
                    <button className="icon-btn" onClick={() => openModal(tuition)}>
                      <FiEdit2 />
                    </button>
                    <button className="icon-btn danger" onClick={() => handleDelete(tuition._id)}>
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Tuition Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTuition ? '청구 수정' : '청구 등록'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>학생 *</label>
                  <select
                    value={formData.studentId}
                    onChange={(e) => handleStudentChange(e.target.value)}
                    required
                    disabled={!!editingTuition}
                  >
                    <option value="">선택하세요</option>
                    {students.map((s) => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>청구월 *</label>
                  <input
                    type="month"
                    value={formData.billingMonth}
                    onChange={(e) => setFormData({ ...formData, billingMonth: e.target.value })}
                    required
                    disabled={!!editingTuition}
                  />
                </div>
              </div>

              {/* 청구 항목들 */}
              <div className="form-group">
                <label>
                  청구 항목 *
                  <button type="button" className="btn btn-sm btn-secondary" onClick={addItem} style={{ marginLeft: '8px' }}>
                    + 항목 추가
                  </button>
                </label>
                {formData.items.length === 0 ? (
                  <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                    학생을 선택하면 소속 반의 수강료가 자동 추가됩니다. 또는 직접 항목을 추가하세요.
                  </p>
                ) : (
                  <div className="tuition-items">
                    {formData.items.map((item, index) => (
                      <div key={index} className="tuition-item-row">
                        <select
                          value={item.classGroupId || ''}
                          onChange={(e) => handleClassSelect(index, e.target.value)}
                          style={{ flex: 1 }}
                        >
                          <option value="">반 선택 (직접입력)</option>
                          {getStudentClasses().map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name} {c.tuitionFee > 0 && `(${formatMoney(c.tuitionFee)})`}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) => updateItem(index, 'name', e.target.value)}
                          placeholder="항목명"
                          style={{ flex: 1 }}
                        />
                        <input
                          type="number"
                          value={item.amount || ''}
                          onChange={(e) => updateItem(index, 'amount', e.target.value)}
                          placeholder="금액"
                          min="0"
                          style={{ width: '120px' }}
                        />
                        <button
                          type="button"
                          className="icon-btn danger"
                          onClick={() => removeItem(index)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>할인</label>
                  <input
                    type="number"
                    value={formData.discount || ''}
                    onChange={(e) => setFormData({ ...formData, discount: Number(e.target.value) || 0 })}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>할인 사유</label>
                  <input
                    type="text"
                    value={formData.discountReason}
                    onChange={(e) => setFormData({ ...formData, discountReason: e.target.value })}
                    placeholder="예: 형제할인"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>납부기한</label>
                  <input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>최종 청구액</label>
                  <input
                    type="text"
                    value={formatMoney(getTotalAmount() - (formData.discount || 0))}
                    disabled
                    style={{ fontWeight: 'bold' }}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>비고</label>
                <input
                  type="text"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="청구 관련 메모"
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingTuition ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedTuition && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>수납 처리</h3>
              <button className="close-btn" onClick={() => setShowPaymentModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handlePayment}>
              <div className="form-group">
                <label>학생</label>
                <input type="text" value={selectedTuition.studentId?.name || '-'} disabled />
              </div>
              <div className="form-group">
                <label>미납액</label>
                <input
                  type="text"
                  value={formatMoney((selectedTuition.finalAmount || selectedTuition.totalAmount) - (selectedTuition.paidAmount || 0))}
                  disabled
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>납부액 *</label>
                  <input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    min="0"
                    max={(selectedTuition.finalAmount || selectedTuition.totalAmount) - (selectedTuition.paidAmount || 0)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>납부일 *</label>
                  <input
                    type="date"
                    value={paymentData.paidAt}
                    onChange={(e) => setPaymentData({ ...paymentData, paidAt: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>결제방법</label>
                <select
                  value={paymentData.method}
                  onChange={(e) => setPaymentData({ ...paymentData, method: e.target.value })}
                >
                  <option value="transfer">계좌이체</option>
                  <option value="cash">현금</option>
                  <option value="card">카드</option>
                </select>
              </div>
              <div className="form-group">
                <label>비고</label>
                <input
                  type="text"
                  value={paymentData.note}
                  onChange={(e) => setPaymentData({ ...paymentData, note: e.target.value })}
                  placeholder="수납 관련 메모"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiCheck /> 수납 처리
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Billing Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>반별 일괄 청구</h3>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>반 선택 *</label>
                <select
                  value={bulkData.classGroupId}
                  onChange={(e) => handleBulkClassChange(e.target.value)}
                >
                  <option value="">반을 선택하세요</option>
                  {classes.filter(c => c.students?.length > 0).map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} ({c.students?.length || 0}명)
                      {c.tuitionFee > 0 && ` - ${formatMoney(c.tuitionFee)}`}
                    </option>
                  ))}
                </select>
              </div>

              {bulkData.classGroupId && (
                <>
                  <div className="bulk-info">
                    <p><strong>학생 수:</strong> {getClassStudents().length}명</p>
                  </div>

                  <div className="form-group">
                    <label>수강료 *</label>
                    <input
                      type="number"
                      value={bulkData.tuitionAmount}
                      onChange={(e) => setBulkData({ ...bulkData, tuitionAmount: e.target.value })}
                      placeholder="수강료를 입력하세요"
                      min="0"
                    />
                    <small className="text-muted">반 설정된 수강료가 기본값으로 입력됩니다. 직접 수정 가능합니다.</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>청구월 *</label>
                      <input
                        type="month"
                        value={bulkData.billingMonth}
                        onChange={(e) => setBulkData({ ...bulkData, billingMonth: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>납부기한</label>
                      <input
                        type="date"
                        value={bulkData.dueDate}
                        onChange={(e) => setBulkData({ ...bulkData, dueDate: e.target.value })}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* 결과 표시 */}
              {bulkResult && (
                <div className="upload-result">
                  <div className="upload-summary">
                    <span className="success-count">성공: {bulkResult.success}건</span>
                    {bulkResult.failed > 0 && (
                      <span className="fail-count">실패: {bulkResult.failed}건</span>
                    )}
                  </div>
                  {bulkResult.errors.length > 0 && (
                    <div className="upload-errors">
                      <h5>실패 사유:</h5>
                      <ul>
                        {bulkResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>
                닫기
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBulkSubmit}
                disabled={!bulkData.classGroupId}
              >
                <FiUsers /> 일괄 청구 생성
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedTuition && (
        <div className="modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>환불 처리</h3>
              <button className="close-btn" onClick={() => setShowRefundModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleRefund}>
              <div className="form-group">
                <label>학생</label>
                <input type="text" value={selectedTuition.studentId?.name || '-'} disabled />
              </div>
              <div className="form-group">
                <label>납부액</label>
                <input type="text" value={formatMoney(selectedTuition.paidAmount || 0)} disabled />
              </div>
              <div className="form-group">
                <label>환불액 *</label>
                <input
                  type="number"
                  value={refundData.amount}
                  onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                  min="0"
                  max={selectedTuition.paidAmount || 0}
                  required
                />
              </div>
              <div className="form-group">
                <label>환불 사유</label>
                <input
                  type="text"
                  value={refundData.reason}
                  onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                  placeholder="예: 수강 취소"
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRefundModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-danger">
                  <FiRotateCcw /> 환불 처리
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
